import React, {useEffect, useRef} from 'react';
import styled from "styled-components";
import Swiper from 'swiper';
import Icon from "@atoms/icon";
import CloseIcon from '@icons/close.svg';
import RadiusGenerate from "@helpers/radius.generate";
import {isEmpty} from "@helpers/helpers";

function GalleryFullscreen({items, slide_to = 0, show, setShow}) {
    const fullscreenSwiper = useRef(null);

    useEffect(() => {
        fullscreenSwiper.current = new Swiper('.fullscreen-swiper-slider', {
            spaceBetween: 8,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'fraction',
            },
            observer: true,
            observeParents: true,
            observeSlideChildren: true,
            keyboard: {
                enabled: true,
            },
        });
    }, []);

    if (fullscreenSwiper.current !== null) {
        fullscreenSwiper.current.slideTo(slide_to, 500, false);
    }

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (fullscreenSwiper.current !== null && show) {
                const slider_length = fullscreenSwiper.current.slides.length;
                const active_index = fullscreenSwiper.current.activeIndex;

                if (e.keyCode === 37 && (active_index + 1 <= slider_length)) {
                    fullscreenSwiper.current.slideTo(active_index + 1, 500, false);
                }

                if (e.keyCode === 39 && (active_index - 1 >= 0)) {
                    fullscreenSwiper.current.slideTo(active_index - 1, 500, false);
                }
            }

            if (e.keyCode === 27 && show) {
                setShow({show: false, slideTo: 0})
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    });

    return (
        <Fullscreen show={show} className="fullscreen-swiper-container">
            <FullscreenClose className="icon-btn mr-8" onClick={() => setShow({show: false, slideTo: 0})}>
                <Icon width="16" height="16"><CloseIcon/></Icon>
            </FullscreenClose>

            <div className="fullscreen-swiper-slider swiper-container">
                <div className="swiper-wrapper">
                    {
                        items.map((item, key) =>
                            <div className="swiper-slide" key={'f-' + key}>
                                <img src={item} alt="" className="img-cover"/>
                            </div>
                        )
                    }
                </div>

                <div className="swiper-pagination align-items-center justify-content-center flex-row-reverse"/>
                <div className="swiper-button-prev"/>
                <div className="swiper-button-next"/>
            </div>
        </Fullscreen>
    )
}

export default React.memo(GalleryFullscreen);

const FullscreenClose = styled.div`
    position: absolute !important;
    top: 24px;
    right: 16px;
    color: ${props => props.theme.black};
    padding: 5px;
    font-size: 50px;
    cursor: pointer;
    z-index: 10;
    line-height: 0;
`;

const Fullscreen = styled.div`
    display: ${props => props.show ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: #fff;
    z-index: 5040;
    background-color: #ffffff;
    
    .swiper-container {
        border-radius: 0;
        height: inherit;
        width: 100%;
        margin-top: 48px;
        max-height: calc(100% - 110px);
        max-width: 960px;
        overflow: hidden;
        position: static;
        
        .swiper-button-prev {
            right: 10px;
            left: auto;
            top: 50%;
            
            &:after {
                content: 'next';
            }
        }
        
        .swiper-button-next {
            top: 50%;
            left: 10px;
            right: auto;
            
            &:after {
                content: 'prev';
            }
        }
        
        .swiper-button-prev, .swiper-button-next {
            width: auto;
            height: auto; 
            &:after {
                display: flex;
                align-items: center;
                justify-content: center;
                color: #222;
                font-weight: 500;
                font-size: 24px;
                width: 54px;
                height: 54px;
                box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.12);
                background-color: #ffffff;
                ${RadiusGenerate('50px')}
            }
        }
        .swiper-button-prev.swiper-button-disabled, .swiper-button-next.swiper-button-disabled {
            opacity: 0;
        }
        .swiper-pagination{
            bottom: 24px;
        }
        
        @media (min-width: ${props => props.theme.screenXsMax}) {
            .swiper-pagination {
                display: none;
            }
        }
        @media (max-width: ${props => props.theme.screenSmMax}) {
            .swiper-button-prev,.swiper-button-next {
                display: none;
            }
        }
        
        .swiper-pagination {
            display: flex !important;
            color: ${props => props.theme.black};
        }
        
        img {
            width: auto !important;
            height: auto !important;
            max-width: 100%;
            max-height: 100%;
            margin: 0 auto;
            display: block;
            border: none !important;
            position: absolute;
            left: 0;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            background-color: ${props => props.theme.gray100};
            
            @media (min-width: ${props => props.theme.screenSmMax}) {
                border-radius: 16px;
            }
        }
    }
`;